import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatRut } from "@/lib/rut";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, QrCode, XCircle } from "lucide-react";

export const clpTP = (n) => "$" + Math.round(Number(n || 0)).toLocaleString("es-CL");
const esQR = (v) => /^\d{20}$/.test(String(v || "").replace(/\s/g, ""));
const errTxt = (e, d) => { const x = e?.response?.data?.detail; return typeof x === "string" ? x : d; };

/** Ficha del cliente de Tarjeta Propia: cupo autorizado, deuda y disponible. */
export function FichaTP({ r, extra }) {
  return (
    <div data-testid="tp-ficha" className="rounded-xl bg-white border border-slate-200 p-3 text-left">
      <p className="font-bold text-slate-900 leading-tight" data-testid="tp-ficha-nombre">{r.nombre}</p>
      <p className="text-xs text-slate-500 font-mono-num">{formatRut(r.rut)}</p>
      <div className="grid grid-cols-3 gap-2 mt-2 text-center">
        {[["Cupo", r.cupo, "text-slate-700"], r.deuda < 0 ? ["Saldo a favor", -r.deuda, "text-emerald-700"] : ["Deuda", r.deuda, "text-rose-700"], ["Disponible", r.disponible, "text-emerald-700"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg bg-slate-50 py-1.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{l}</p>
            <p className={`font-mono-num font-bold text-sm ${c}`} data-testid={`tp-ficha-${l.toLowerCase()}`}>{clpTP(v)}</p>
          </div>
        ))}
      </div>
      {extra}
    </div>
  );
}

/** Caja: valida la Tarjeta Propia por QR (sin clave) o RUT + clave antes de cobrar. */
export default function TPValidar({ nombre, monto, auth, onAuth }) {
  const [codigo, setCodigo] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEffect(() => { setTimeout(() => ref.current?.focus(), 80); }, []);
  useEffect(() => { if (auth && Math.abs(auth.monto - monto) > 0.5) onAuth(null); }, [monto]); // eslint-disable-line react-hooks/exhaustive-deps
  const validar = async (cod = codigo) => {
    const qr = esQR(cod);
    if (!cod.trim()) { setError("Escanea el QR o escribe el RUT"); return; }
    if (!qr && !/^\d{4}$/.test(pin)) { setError("Sin QR, pide la clave de 4 dígitos del cliente"); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/tarjeta-propia/autorizar", qr ? { codigo: cod.trim(), monto } : { rut: cod.trim(), pin, monto });
      onAuth({ ...data, monto });
    } catch (e) { setError(errTxt(e, "No se pudo validar la tarjeta")); onAuth(null); }
    setBusy(false); setPin("");
  };
  if (auth) {
    return (
      <FichaTP r={auth} extra={
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1" data-testid="tp-aprobada">
            <CheckCircle2 className="w-4 h-4" /> Cupo suficiente · queda {clpTP(auth.disponible_despues)}
          </p>
          <button type="button" className="text-xs font-semibold text-slate-500 hover:text-slate-800" onClick={() => onAuth(null)} data-testid="tp-cambiar">Cambiar</button>
        </div>} />
    );
  }
  const qr = esQR(codigo);
  return (
    <div className="space-y-2 text-left" data-testid="tp-validar">
      <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><QrCode className="w-4 h-4 text-sky-600" /> Escanea el QR de la {nombre} o escribe el RUT</p>
      <Input ref={ref} value={codigo} data-testid="tp-codigo" placeholder="QR o RUT del cliente" autoComplete="off"
             onChange={(e) => { setCodigo(e.target.value); setError(null); }}
             onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); if (esQR(codigo)) validar(); } }}
             className="h-11 rounded-xl font-mono-num" />
      {!qr && codigo.trim() && (
        <Input type="password" inputMode="numeric" maxLength={4} value={pin} data-testid="tp-pin" placeholder="Clave de 4 dígitos del cliente"
               onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); validar(); } }}
               className="h-11 rounded-xl font-mono-num tracking-[0.5em] text-center" />
      )}
      {error && <p data-testid="tp-error" className="text-sm font-semibold text-rose-600 flex items-start gap-1.5"><XCircle className="w-4 h-4 mt-0.5 shrink-0" />{error}</p>}
      <Button type="button" disabled={busy} onClick={() => validar()} data-testid="tp-validar-btn" className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Validar cupo por ${clpTP(monto)}`}
      </Button>
    </div>
  );
}
